import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AssistantChatMessage,
  AssistantChatResponse,
  AssistantModelOption,
  AssistantSettings,
  AssistantStatus,
  UpdateAssistantSettingsInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const assistantKeys = {
  status: ['assistant', 'status'] as const,
  settings: ['assistant', 'settings'] as const,
  models: ['assistant', 'models'] as const,
};

/** Whether the chat is available to the current member (configured + enabled). */
export function useAssistantStatus(): UseQueryResult<AssistantStatus> {
  return useQuery({
    queryKey: assistantKeys.status,
    queryFn: () => api.get<AssistantStatus>('/assistant/status'),
    staleTime: 60_000,
  });
}

/** Sends the running conversation and returns the assistant's reply + tool trace. */
export function sendAssistantChat(
  messages: AssistantChatMessage[],
): Promise<AssistantChatResponse> {
  return api.post<AssistantChatResponse>('/assistant/chat', { messages });
}

/** Admin-only assistant settings. */
export function useAssistantSettings(): UseQueryResult<AssistantSettings> {
  return useQuery({
    queryKey: assistantKeys.settings,
    queryFn: () => api.get<AssistantSettings>('/assistant/settings'),
  });
}

/** Admin-only: live Claude models available to the stored API key. */
export function useAssistantModels(enabled: boolean): UseQueryResult<AssistantModelOption[]> {
  return useQuery({
    queryKey: assistantKeys.models,
    queryFn: () => api.get<AssistantModelOption[]>('/assistant/models'),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useUpdateAssistantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAssistantSettingsInput) =>
      api.put<AssistantSettings>('/assistant/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assistantKeys.settings });
      void queryClient.invalidateQueries({ queryKey: assistantKeys.status });
    },
  });
}
